import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, Pin, Shield, MessageSquare, UserX, Check, X, Flag, ArrowLeft, Plus } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import api, { formatApiError } from "../lib/api";

const panel = "rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] p-4";
const field = "w-full rounded-md border border-[var(--border-subtle)] bg-[var(--bg-base)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]";
const btn = "inline-flex items-center justify-center gap-1.5 rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-bold uppercase tracking-wide text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)] disabled:opacity-50";
const ghost = "inline-flex items-center justify-center gap-1.5 rounded-md border border-[var(--border-subtle)] px-3 py-1.5 text-xs font-semibold text-[var(--text-primary)] hover:border-[var(--accent)] disabled:opacity-50";

export default function CommunitySpace() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [threads, setThreads] = useState([]);
  const [pending, setPending] = useState(0);
  const [tab, setTab] = useState("threads");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", pinned: false });
  const [showForm, setShowForm] = useState(false);
  const [mod, setMod] = useState({ threads: [], posts: [] });
  const [members, setMembers] = useState({ members: [], banned: [] });

  const cid = data?.community?.id;
  const c = data?.community;

  const loadThreads = useCallback(async (id) => {
    const { data: t } = await api.get(`/communities/${id}/threads`);
    setThreads(t.threads); setPending(t.pending_count || 0);
  }, []);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/communities/film/${slug}`);
      setData(d);
      await loadThreads(d.community.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load space"); }
  }, [slug, loadThreads]);

  useEffect(() => { load(); }, [load]);

  const join = async () => {
    setBusy(true);
    try { await api.post(`/communities/${cid}/join`); toast.success("You joined the space ★"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); }
  };

  const createThread = async () => {
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/communities/${cid}/threads`, form);
      toast.success(r.pending ? "Posted — awaiting review before it goes live." : "Thread posted ★");
      setForm({ title: "", body: "", pinned: false }); setShowForm(false);
      await loadThreads(cid);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); }
  };

  const loadMod = async () => { const { data: m } = await api.get(`/communities/${cid}/moderation`); setMod(m); };
  const loadMembers = async () => { const { data: m } = await api.get(`/communities/${cid}/members`); setMembers(m); };

  const moderate = async (target_type, target_id, action) => {
    try { await api.post(`/communities/${cid}/moderate`, { target_type, target_id, action }); toast.success(`${action}d`); await loadMod(); await loadThreads(cid); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const ban = async (uid) => { try { await api.post(`/communities/${cid}/ban`, { user_id: uid }); toast.success("Member banned"); await loadMembers(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); } };
  const unban = async (uid) => { try { await api.post(`/communities/${cid}/unban`, { user_id: uid }); toast.success("Member unbanned"); await loadMembers(); } catch (e) { toast.error("Failed"); } };

  useEffect(() => { if (cid && tab === "moderation") loadMod(); if (cid && tab === "members") loadMembers(); /* eslint-disable-next-line */ }, [tab, cid]);

  if (!data) return (
    <div className="min-h-screen bg-[var(--bg-base)]" data-skin="classic-red"><Navbar /><div className="flex items-center justify-center gap-2 p-16 text-[var(--text-secondary)]"><Loader2 className="animate-spin" size={18} /> Loading…</div></div>
  );

  return (
    <div className="min-h-screen bg-[var(--bg-base)]" data-skin="classic-red" data-testid="community-space">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 py-6">
        <Link to={`/title/indie-${c.film_slug}`} className="inline-flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--accent-hover)]" data-testid="community-back-film"><ArrowLeft size={14} /> Back to film</Link>

        <header className="mt-4 flex items-start gap-4">
          {c.film_poster ? <img src={c.film_poster} alt="" className="h-28 w-20 flex-none rounded object-cover" /> : null}
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold uppercase tracking-widest text-[var(--accent-hover)]">Creator Space</p>
            <h1 className="font-head text-2xl text-[var(--text-primary)]" data-testid="community-title">{c.film_title}</h1>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">{c.creator_name ? <>Hosted by <span className="font-semibold text-[var(--text-primary)]">{c.creator_name}</span> · </> : null}{c.member_count} member{c.member_count === 1 ? "" : "s"}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {c.is_banned ? (
                <span className="rounded-md bg-red-500/15 px-3 py-1.5 text-xs font-semibold text-red-400" data-testid="community-banned">You are banned from this space</span>
              ) : c.is_member || c.is_creator ? (
                <span className="inline-flex items-center gap-1 rounded-md bg-[var(--accent)]/15 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-[var(--accent-hover)]" data-testid="community-joined">{c.is_creator ? <><Shield size={13} /> Filmmaker</> : <><Check size={13} /> Joined</>}</span>
              ) : (
                <button onClick={join} disabled={busy} className={btn} data-testid="community-join-button">{busy ? <Loader2 size={14} className="animate-spin" /> : null} Join to discuss</button>
              )}
            </div>
          </div>
        </header>

        <div className="mt-6 flex gap-2 border-b border-[var(--border-subtle)]">
          <button onClick={() => setTab("threads")} className={`px-3 py-2 text-sm font-semibold ${tab === "threads" ? "border-b-2 border-[var(--accent)] text-[var(--text-primary)]" : "text-[var(--text-secondary)]"}`} data-testid="community-tab-threads">Discussions</button>
          {c.can_moderate && <button onClick={() => setTab("moderation")} className={`px-3 py-2 text-sm font-semibold ${tab === "moderation" ? "border-b-2 border-[var(--accent)] text-[var(--text-primary)]" : "text-[var(--text-secondary)]"}`} data-testid="community-tab-moderation">Review{pending ? ` (${pending})` : ""}</button>}
          {c.can_moderate && <button onClick={() => setTab("members")} className={`px-3 py-2 text-sm font-semibold ${tab === "members" ? "border-b-2 border-[var(--accent)] text-[var(--text-primary)]" : "text-[var(--text-secondary)]"}`} data-testid="community-tab-members">Members</button>}
        </div>

        {tab === "threads" && (
          <div className="mt-4 space-y-3">
            {(c.is_member || c.is_creator) && !c.is_banned && (
              showForm ? (
                <div className={panel} data-testid="community-thread-form">
                  <input className={field} placeholder="Topic title" value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))} data-testid="thread-title-input" />
                  <textarea className={`${field} mt-2 min-h-[90px]`} placeholder="Say something about the film…" value={form.body} onChange={(e) => setForm((s) => ({ ...s, body: e.target.value }))} data-testid="thread-body-input" />
                  {c.is_creator && <label className="mt-2 flex items-center gap-2 text-xs text-[var(--text-secondary)]"><input type="checkbox" checked={form.pinned} onChange={(e) => setForm((s) => ({ ...s, pinned: e.target.checked }))} className="accent-[var(--accent)]" data-testid="thread-pin-checkbox" /> Pin this thread</label>}
                  <div className="mt-3 flex gap-2">
                    <button onClick={createThread} disabled={busy || !form.title.trim()} className={btn} data-testid="thread-submit">{busy ? <Loader2 size={14} className="animate-spin" /> : null} Post</button>
                    <button onClick={() => setShowForm(false)} className={ghost}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowForm(true)} className={`${btn} w-full`} data-testid="community-new-thread"><Plus size={15} /> Start a thread</button>
              )
            )}
            {threads.length === 0 ? (
              <p className="py-8 text-center text-sm text-[var(--text-secondary)]" data-testid="community-no-threads">No discussions yet. {c.is_creator ? "Start the first one!" : ""}</p>
            ) : threads.map((t) => (
              <button key={t.id} onClick={() => navigate(`/community/film/${c.film_slug}/thread/${t.id}`)} className={`${panel} block w-full text-left hover:border-[var(--accent)]`} data-testid={`thread-row-${t.id}`}>
                <div className="flex items-center gap-2">
                  {t.pinned && <Pin size={13} className="flex-none text-[var(--accent-hover)]" />}
                  <span className="truncate font-bold text-[var(--text-primary)]">{t.title}</span>
                </div>
                <p className="mt-1 flex items-center gap-3 text-xs text-[var(--text-muted)]"><span>{t.author_name}</span><span className="inline-flex items-center gap-1"><MessageSquare size={11} /> {t.reply_count}</span></p>
              </button>
            ))}
          </div>
        )}

        {tab === "moderation" && c.can_moderate && (
          <div className="mt-4 space-y-3" data-testid="community-moderation">
            {mod.threads.length === 0 && mod.posts.length === 0 ? (
              <p className="py-8 text-center text-sm text-[var(--text-secondary)]" data-testid="moderation-empty">Nothing waiting for review.</p>
            ) : (
              <>
                {mod.threads.map((t) => (
                  <div key={t.id} className={panel} data-testid={`mod-thread-${t.id}`}>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">New thread · {t.author_name}</p>
                    <p className="mt-1 font-bold text-[var(--text-primary)]">{t.title}</p>
                    <p className="mt-1 text-sm text-[var(--text-secondary)]">{t.body}</p>
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => moderate("thread", t.id, "approve")} className={ghost} data-testid={`mod-approve-${t.id}`}><Check size={13} /> Approve</button>
                      <button onClick={() => moderate("thread", t.id, "reject")} className={ghost}><X size={13} /> Reject</button>
                    </div>
                  </div>
                ))}
                {mod.posts.map((p) => (
                  <div key={p.id} className={panel} data-testid={`mod-post-${p.id}`}>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Reply by {p.author_name} · on “{p.thread_title}”</p>
                    <p className="mt-1 text-sm text-[var(--text-secondary)]">{p.body}</p>
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => moderate("post", p.id, "approve")} className={ghost} data-testid={`mod-approve-post-${p.id}`}><Check size={13} /> Approve</button>
                      <button onClick={() => moderate("post", p.id, "reject")} className={ghost}><X size={13} /> Reject</button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {tab === "members" && c.can_moderate && (
          <div className="mt-4 space-y-4" data-testid="community-members">
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)]">Members ({members.members.length})</p>
              <div className="space-y-2">
                {members.members.length === 0 ? <p className="text-sm text-[var(--text-muted)]">No members yet.</p> : members.members.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 rounded-md border border-[var(--border-subtle)] p-2" data-testid={`member-row-${m.id}`}>
                    <span className="min-w-0 flex-1 truncate text-sm text-[var(--text-primary)]">{m.name} <span className="text-[var(--text-muted)]">@{m.username}</span></span>
                    <button onClick={() => ban(m.id)} className={ghost} data-testid={`member-ban-${m.id}`}><UserX size={13} /> Ban</button>
                  </div>
                ))}
              </div>
            </div>
            {members.banned.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)]">Banned ({members.banned.length})</p>
                <div className="space-y-2">
                  {members.banned.map((m) => (
                    <div key={m.id} className="flex items-center gap-3 rounded-md border border-[var(--border-subtle)] p-2">
                      <span className="min-w-0 flex-1 truncate text-sm text-[var(--text-muted)]">{m.name} @{m.username}</span>
                      <button onClick={() => unban(m.id)} className={ghost}>Unban</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
